import { Icon } from '@iconify/react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCreateHttpRequestActionForm } from '../../../hooks/Action/ApiAction/useCreateHttpRequestActionForm.hook';
import { Button } from '../../Shared/Button/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../Shared/Form/Form';
import { AutosizeTextarea } from '../../Shared/Textarea/Textarea';
import { QueryKey } from '@/Config/api/queryKey';
import { pushGtmEvent } from '@/Config/libs/analytics';
import { HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { GtmEventEnum } from '@/Domain/enum/GtmEvent.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export type HttpRequestCreateFromTextInput = {
  process_id: string | number;
  user_input: string;
};

export const CreateHttpRequestActionDialog = () => {
  const { t } = useTranslation('http_request_ai_action', { keyPrefix: 'create_modal' });
  const { isOpen, closeModal, toggleModal } = useModalStore();
  const { capture } = usePostHogAnalytics();

  const { form, handleSubmit } = useCreateHttpRequestActionForm();

  const handleNeedHelp = () => window.open('https://docs.quickchat.ai/', '_blank');

  const createHttpRequestActionMutation = useMutation<HttpActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.httpRequest.create],
  });
  const handleCreateHttpActionManually = async () => {
    const httpRequestAction = await createHttpRequestActionMutation.mutateAsync();
    capture(PostHogEventEnum.ActionCreated, { action_type: 'http_request' });
    pushGtmEvent(GtmEventEnum.ActionCreated, { action_type: 'http_request' });
    toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET, { httpRequestAction });
  };

  return (
    <Dialog open={isOpen(MODAL_NAMES.CREATE_HTTP_REQUEST_ACTION_DIALOG)} onOpenChange={closeModal}>
      <DialogContent className="w-lg">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} autoComplete="off" className="space-y-4">
            <DialogHeader>
              <DialogTitle>{t('title')}</DialogTitle>
              <DialogDescription>{t('description1')}</DialogDescription>
            </DialogHeader>
            <FormField
              name="action_description"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <AutosizeTextarea
                      data-testid="http-request-action-from-text-description-input"
                      placeholder={t('placeholder')}
                      minHeight={250}
                      maxHeight={400}
                      className="px-4 mb-1"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogDescription>{t('description2')}</DialogDescription>
            <DialogFooter className="flex flex-row !justify-between items-center">
              <Button variant="link" onClick={handleNeedHelp} className="!pl-0">
                <Icon icon="mingcute:question-line" className="size-5 text-muted-foreground  " />
                <span className="!text-muted-foreground text-sm">{t('button.need_help')}</span>
              </Button>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={handleCreateHttpActionManually}
                  type="button"
                  data-testid="http-request-action-from-text-create-manually-button"
                >
                  {t('button.create_manually')}
                </Button>

                <Button
                  variant="dark"
                  type="submit"
                  data-testid="http-request-action-from-text-submit-button"
                >
                  {t('button.submit')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
